<form [formGroup]="formGroup" class="tax-form">

    <ng-container>

        <!-- Table Actions (if provided) -->
        <div *ngIf="tableActions && tableActions.length > 0 && !viewMode" class="table-actions-container flex justify-end gap-2 mb-2">
            <ng-container *ngFor="let action of tableActions">
                <p-button
                    *ngIf="isTableActionVisible(action)"
                    [label]="action.label"
                    [icon]="action.icon"
                    [disabled]="isTableActionDisabled(action)"
                    [styleClass]="'compact-btn'"
                    (onClick)="action.onClick()">
                </p-button>
            </ng-container>
        </div>

        <!-- PrimeNG Table -->
        <div [formArrayName]="tableKey" class="card rounded-lg bg-[#fafbfc] overflow-x-auto" #scrollContainer (wheel)="onTableWheel($event, scrollContainer)">
            <p-table [value]="getVisibleTableRows()" [tableStyle]="{'min-width':'100%'}" [size]="'small'">

                <!-- Header -->
                <ng-template pTemplate="header">
                    <tr>
                        <!-- Selection Checkbox Column - First (if enabled) -->
                        <th *ngIf="showSelectionCheckbox && !viewMode" class="text-sm text-center" style="width: 50px; min-width: 50px;">
                            <div class="checkbox-single dynamic-checkbox">
                                <input type="checkbox" 
                                [checked]="isAllSelected()"
                                [indeterminate]="isIndeterminate()"
                                (change)="toggleSelectAll($event)"
                                [id]="'selectAll_' + tableKey"
                                class="checkbox-single w-full min-w-full" />
                            </div>
                        </th>
                        <!-- Serial Number Column -->
                        <th *ngIf="showSerialNumber" class="text-sm text-center" style="width: 80px; min-width: 80px;">
                            S.No
                        </th>
                        <!-- Duplicate/Split column header - at the front -->
                        <th *ngIf="!viewMode && showDuplicateRowButton" class="duplicate-button-column text-sm text-center" style="width: 50px; min-width: 50px; background-color: #fafbfc;">
                        </th>
                        <ng-container *ngFor="let col of dynamicFormFields">
                            <th *ngIf="col.is_visible !== false && (!shouldHideField || !shouldHideField(col, undefined, undefined))"
                                [class.text-right]="isNumericField(col)"
                                class="text-sm"
                                [style.width]="getColumnWidth(col)"
                                [style.min-width]="getColumnMinWidth(col)">
                                {{ col.display_name || col.field_name }}
                                <span *ngIf="col.is_mandatory" class="text-red-500 ml-1">*</span>
                            </th>
                        </ng-container>
                        <!-- Extra columns header (e.g., fulfillment status/actions) -->
                        <ng-container *ngIf="extraColumns">
                            <th *ngFor="let extraCol of extraColumns" class="text-sm" [style.width]="extraCol.width || 'auto'" [style.min-width]="extraCol.width || '100px'">
                                {{ extraCol.header }}
                            </th>
                        </ng-container>
                        <!-- Custom action column header - at the end (if provided and no add/remove buttons) -->
                        <th *ngIf="customActionColumn && !showAddRemoveButtons" class="custom-action-column text-sm text-center" style="width: 60px; min-width: 60px; position: sticky; right: 0; background-color: #fafbfc; z-index: 10;">
                        </th>
                        <!-- Add button column - always at the end -->
                        <th *ngIf="!viewMode && showAddRemoveButtons && showAddButton" class="add-button-column" style="width: 80px; min-width: 80px; position: sticky; right: 0; background-color: #fafbfc; z-index: 10;">
                            <!-- Add Row button -->
                            <p-button icon="pi pi-plus-circle" [rounded]="true" [outlined]="true" [text]="true"
                                (click)="addTableRow()">
                            </p-button>
                        </th>
                    </tr>
                </ng-template>

                <!-- Body -->
                <ng-template pTemplate="body" let-rowData>
                    <tr [formGroup]="rowData.control">
                        <!-- Selection Checkbox Column - First (if enabled) -->
                        <td *ngIf="showSelectionCheckbox && !viewMode">
                            <div class="checkbox-single dynamic-checkbox">
                                <input  type="checkbox" class="checkbox-single" 
                                [formControlName]="'_selected'"
                                (change)="onSelectionChange(rowData.index, $event)" />
                            </div>
                        </td>
                        <!-- Serial Number Column -->
                        <td *ngIf="showSerialNumber" class="text-sm text-center">
                            {{ rowData.index + 1 }}
                        </td>

                        <!-- Duplicate/Split button (only in edit mode) - at the front -->
                        <td class="text-center duplicate-button-column" *ngIf="!viewMode && showDuplicateRowButton && !(shouldHideDuplicateButton && shouldHideDuplicateButton(rowData.control))" style="width: 50px; min-width: 50px; background-color: #fafbfc;">
                            <p-button icon="pi pi-clone" [rounded]="true"
                                [outlined]="true" [text]="true" severity="info" (click)="duplicateRow(rowData.index)"
                                pTooltip="Split this item into multiple rows" tooltipPosition="top">
                            </p-button>
                        </td>
                        <ng-container *ngFor="let col of dynamicFormFields; let i = index">
                            <td *ngIf="col.is_visible !== false && (!shouldHideField || !shouldHideField(col, rowData.control, rowData.index))"
                                [class.text-right]="isNumericField(col)"
                                [style.width]="getColumnWidth(col)"
                                [style.min-width]="getColumnMinWidth(col)">

                            <ng-container [ngSwitch]="col.field_type">
                                <!-- Redirect field renders as link in both modes -->
                                <a *ngSwitchCase="'redirect'"
                                   class="text-sm text-primary underline cursor-pointer"
                                   (click)="openRedirectInNewTab(col, rowData.control, $event)"
                                   [attr.aria-label]="'Open ' + getTableCellDisplayValue(col, rowData.control, rowData.index) + ' in new tab'">
                                    {{ getTableCellDisplayValue(col, rowData.control, rowData.index) }}
                                </a>

                                <!-- Label / read-only fields -->
                                <span *ngSwitchCase="'label'" class="text-sm">
                                    <span *ngIf="colorDotMap?.has(rowData.index + '_' + col.field_name)"
                                        style="width: 10px; height: 10px; border-radius: 50%; display: inline-block; vertical-align: middle; margin-right: 6px;"
                                        [style.background-color]="colorDotMap.get(rowData.index + '_' + col.field_name)"></span>
                                    {{ getFormattedCellValue(col, rowData) }}
                                </span>

                                <!-- Status pill — colored p-tag driven by col.statusConfig -->
                                <ng-container *ngSwitchCase="'status'">
                                    <p-tag
                                        [value]="(col.statusLabels && col.statusLabels[getStatusKey(col, rowData.control)]) || getStatusKey(col, rowData.control) || ''"
                                        [severity]="$any((col.statusConfig && col.statusConfig[getStatusKey(col, rowData.control)]) || 'secondary')"
                                        [styleClass]="'status-' + (getStatusKey(col, rowData.control) || '').toLowerCase().split(' ').join('-').split('_').join('-')">
                                    </p-tag>
                                </ng-container>

                                <!-- Tag field - shows "primary" text only on first visible row -->
                                <ng-container *ngSwitchCase="'tag'">
                                    <p-tag 
                                        *ngIf="isFirstVisibleRow(rowData.index)"
                                        value="primary"
                                        styleClass="p-tag-primary">
                                    </p-tag>
                                </ng-container>

                                <!-- File upload field -->
                                <ng-container *ngSwitchCase="'file_upload'">
                                    <!-- View mode: Show file name with download link -->
                                    <div *ngIf="viewMode" class="file-view-container">
                                        <ng-container *ngIf="hasFileValue(rowData.index, col.field_name)">
                                            <a class="text-sm text-primary underline cursor-pointer"
                                               (click)="openDownloadUrl(rowData.index, col.field_name); $event.preventDefault()">
                                                {{ getSelectedFileName(col, rowData.index) }}
                                            </a>
                                        </ng-container>
                                        <span *ngIf="!hasFileValue(rowData.index, col.field_name)" class="text-sm text-gray-400">-</span>
                                    </div>
                                    <!-- Edit mode: File upload control -->
                                    <div *ngIf="!viewMode" class="file-upload-container">
                                        <input
                                            type="file"
                                            class="file-input"
                                            [id]="getTableFieldKey(rowData.index, col.field_name)"
                                            [accept]="col.accept || '*'"
                                            [multiple]="col.multiple || false"
                                            (change)="onFileSelected($event, col, rowData.index)"
                                            #tableFileInput
                                        />
                                        <!-- Show Choose File button only when no file is selected -->
                                        <label *ngIf="!getSelectedFileName(col, rowData.index)" [for]="getTableFieldKey(rowData.index, col.field_name)" class="file-upload-btn">
                                            <i class="pi pi-upload"></i>
                                            <span>{{ col.upload_label || 'Choose File' }}</span>
                                        </label>
                                        <!-- Show file name with download link when file is selected -->
                                        <ng-container *ngIf="getSelectedFileName(col, rowData.index)">
                                            <a class="text-sm text-primary underline cursor-pointer"
                                               (click)="openDownloadUrl(rowData.index, col.field_name); $event.preventDefault()">
                                                {{ getSelectedFileName(col, rowData.index) }}
                                            </a>
                                        </ng-container>
                                    </div>
                                </ng-container>

                                <!-- Default rendering -->
                                <ng-container *ngSwitchDefault>
                                    <!-- View mode fallback -->
                                    <span *ngIf="viewMode" class="text-sm">
                                        <span *ngIf="colorDotMap?.has(rowData.index + '_' + col.field_name)"
                                            style="width: 10px; height: 10px; border-radius: 50%; display: inline-block; vertical-align: middle; margin-right: 6px;"
                                            [style.background-color]="colorDotMap.get(rowData.index + '_' + col.field_name)"></span>
                                        {{
                                            shouldFormatCurrency(col)
                                            ? formatCurrencyWithUSD(
                                                +getTableCellDisplayValue(col, rowData.control, rowData.index) || 0
                                                )
                                            : getTableCellDisplayValue(col, rowData.control, rowData.index)
                                        }}
                                        </span>

                                    <!-- Non-editable fields: Show as read-only display value -->
                                    <span *ngIf="!viewMode && isNonEditableField(col)" class="text-sm">
                                        {{ getTableCellDisplayValue(col, rowData.control, rowData.index) }}
                                    </span>

                                    <!-- EDIT MODE: Input controls -->
                                    <ng-container *ngIf="!viewMode && !isNonEditableField(col)">

                                <!-- Text input -->
                                <input *ngIf="col.field_type === 'input' || !col.field_type" pInputText
                                    class="p-inputtext-sm w-full" [formControlName]="getFieldKey(col.field_name)"
                                    [placeholder]="col.display_name" (input)="onTableFieldChange(rowData.index, col.field_name, $event)" />

                                <!-- Number input -->
                                <input *ngIf="col.field_type === 'number'" type="number" pInputText
                                    class="p-inputtext-sm w-full" [formControlName]="getFieldKey(col.field_name)"
                                    [placeholder]="col.display_name" (input)="onTableFieldChange(rowData.index, col.field_name, $event)" />

                                <!-- Decimal / double input -->
                                <input *ngIf="col.field_type === 'double' || col.field_type === 'decimal' || col.field_type === 'float'"
                                    type="number" step="0.01" pInputText
                                    class="p-inputtext-sm w-full" [formControlName]="getFieldKey(col.field_name)"
                                    [placeholder]="col.display_name" (input)="onTableFieldChange(rowData.index, col.field_name, $event)" />

                                <!-- Select dropdown -->
                                 
                                <p-select *ngIf="col.field_type === 'select'" [options]="getFieldOptions(rowData.index, col)"
                                    optionLabel="label" optionValue="value"
                                    appendTo="body"
                                    [formControlName]="getFieldKey(col.field_name)"
                                    [placeholder]="'Select ' + col.display_name" size="small" fluid class="w-full"
                                    [showClear]="!col.is_mandatory"
                                    (onChange)="onTableFieldChange(rowData.index, col.field_name, $event)" [filter]="true"
                                    (onShow)="onShow(rowData.index, i)"
                                    (onHide)="onHide(rowData.index, i)"
                                    (onFilter)="cacheFilter(rowData.index, i)"
                                >
                                </p-select>

                                <!-- Date picker -->
                                <p-datepicker *ngIf="col.field_type === 'date'"
                                    appendTo="body"
                                    [formControlName]="getFieldKey(col.field_name)" dateFormat="dd/mm/yy"
                                    [showIcon]="true" [appendTo]="'body'" [timeOnly]="false" [showTime]="col.show_time || col.showTime || false"
                                    class="w-full" size="small" (onSelect)="onTableFieldChange(rowData.index, col.field_name, $event)">
                                </p-datepicker>

                                <!-- Time picker -->
                                <p-datepicker *ngIf="col.field_type === 'datetime'"
                                    appendTo="body"
                                    [formControlName]="getFieldKey(col.field_name)" [timeOnly]="true"
                                    [hourFormat]="'24'" [showIcon]="false" [appendTo]="'body'" class="w-full"
                                    size="small" (onSelect)="onTableFieldChange(rowData.index, col.field_name, $event)">
                                </p-datepicker>

                                <!-- Checkbox -->
                                <div *ngIf="col.field_type === 'checkbox'" class="checkbox-single dynamic-checkbox">
                                    <input  type="checkbox" class="checkbox-single" 
                                    [formControlName]="getFieldKey(col.field_name)"
                                    (change)="onTableFieldChange(rowData.index, col.field_name, $event)" />
                                </div>

                                <!-- Radio button (without options only) -->
                                <div class="flex small-radio-group items-center gap-2" *ngIf="col.field_type === 'radio'">
                                    <input
                                        type="radio"
                                        class="radio-single small-radio"
                                        [name]="getFieldKey(col.field_name)"
                                        size="small"
                                        [formControlName]="getFieldKey(col.field_name)"
                                        [id]="getFieldKey(col.field_name) + '_' + rowData.index"
                                        [value]="getRadioButtonValue(col)"
                                        [checked]="isRadioRowSelected(rowData.index, col.field_name)"
                                        (change)="onTableFieldChange(rowData.index, col.field_name, $event)"
                                    />
                                </div>

                                <!-- Textarea -->
                                <textarea *ngIf="col.field_type === 'textarea'" rows="2" class="p-inputtextarea w-full"
                                    [formControlName]="getFieldKey(col.field_name)" [placeholder]="col.display_name"
                                    (input)="onTableFieldChange(rowData.index, col.field_name, $event)"></textarea>

                                <!-- Search with Autocomplete -->
                                <p-autocomplete *ngIf="col.field_type === 'autocomplete' || col.field_type === 'search'"
                                    fluid
                                    size="small"
                                    appendTo="body"
                                    [formControlName]="getFieldKey(col.field_name)"
                                    [placeholder]="'Search ' + col.display_name"
                                    [suggestions]="getSearchSuggestions(rowData.index, col)"
                                    [optionLabel]="col.optionLabel || col.autocompleteDisplayField || 'label'"
                                    [dropdown]="true"
                                    [optionValue]="col.returnObject ? undefined : (col.optionValue || 'value')"
                                    [forceSelection]="col.forceSelection !== false"
                                    [group]="col.group || false"
                                    (completeMethod)="onSearchQuery($event, rowData.index, col, rowData.control)"
                                    (onSelect)="onSearchSelect($event, rowData.index, col.field_name)"
                                    class="filter-autocomplete">
                                    <!-- Custom item template with top-right and bottom-right text -->
                                    <ng-template *ngIf="col.useCustomItemTemplate" pTemplate="item" let-item>
                                        <div class="relative w-full py-1.5 px-2 min-h-[3rem]">
                                            <!-- Main label/content -->
                                            <div class="pr-24 flex items-center">
                                                <span class="text-sm text-gray-900">{{ item.label || item.name || item.item_master_name || item.value }}</span>
                                            </div>
                                            <!-- Top right text -->
                                            <div class="absolute top-1 right-2 text-[10px] text-gray-500 leading-tight">
                                                <span *ngIf="getItemTopRightText(item, col)">{{ getItemTopRightText(item, col) }}</span>
                                            </div>
                                            <!-- Bottom right text -->
                                            <div class="absolute bottom-1 right-2 text-[10px] text-gray-500 leading-tight">
                                                <span *ngIf="getItemBottomRightText(item, col)">{{ getItemBottomRightText(item, col) }}</span>
                                            </div>
                                        </div>
                                    </ng-template>
                                    <!-- Group template for grouped autocomplete -->
                                    <ng-template *ngIf="col.group" let-group #group>
                                        <div class="flex items-center">
                                            <span>{{ group.label }}</span>
                                        </div>
                                    </ng-template>
                                </p-autocomplete>

                                <!-- Regex validation error (small red line) -->
                                <div
                                  *ngIf="regexValidationErrors && regexValidationErrors[getRowFieldKey(rowData.index, col.field_name)]"
                                  class="mt-0 pt-0 text-[11px] leading-snug text-red-500"
                                >
                                  {{ regexValidationErrors[getRowFieldKey(rowData.index, col.field_name)] }}
                                </div>
                                    </ng-container>
                                </ng-container>
                            </ng-container>
                                <small class="p-error block text-xs text-red-500 mt-1" *ngIf="!viewMode && getErrorMessage(col, rowData.control)">
                                    {{ getErrorMessage(col, rowData.control) }}
                                </small>
                            </td>
                        </ng-container>

                        <!-- Extra columns body (e.g., fulfillment status/actions) -->
                        <ng-container *ngIf="extraColumns">
                            <td *ngFor="let extraCol of extraColumns; let colIdx = index" [style.width]="extraCol.width || 'auto'">
                                <span *ngIf="extraCol.type === 'text'" class="text-sm">
                                    {{ extraCol.getValue ? extraCol.getValue(rowData.index, rowData.control) : '' }}
                                </span>
                                <p-tag *ngIf="extraCol.type === 'tag'"
                                    [value]="extraCol.getValue ? extraCol.getValue(rowData.index, rowData.control) : ''"
                                    [severity]="$any(extraCol.getTagSeverity ? extraCol.getTagSeverity(rowData.index, rowData.control) : 'info')" />
                                <div *ngIf="extraCol.type === 'actions'" class="flex gap-1">
                                    <ng-container *ngFor="let action of getExtraColumnActions(colIdx, rowData.index, rowData.control); trackBy: trackByIndex">
                                        <p-button *ngIf="action.visible"
                                            [icon]="action.icon"
                                            [rounded]="true"
                                            [text]="true"
                                            [severity]="$any(action.severity)"
                                            [pTooltip]="action.tooltip"
                                            (onClick)="action.onClick()" />
                                    </ng-container>
                                </div>
                            </td>
                        </ng-container>

                        <!-- Custom action column - at the end (if provided and no add/remove buttons) -->
                        <td *ngIf="customActionColumn" class="text-center custom-action-column" style="width: 60px; min-width: 60px; position: sticky; right: 0; background-color: #fafbfc; z-index: 10;">
                            <ng-container *ngIf="getCustomActionConfig(rowData.index, rowData.control) as actionConfig">
                                <!-- Icon button (e.g., trash icon for delete) -->
                                <p-button 
                                    *ngIf="actionConfig.show && actionConfig.svgIcon"
                                    [icon]="actionConfig.svgIcon"
                                    [rounded]="true"
                                    [text]="true"
                                    size="small"
                                    severity="danger"
                                    (onClick)="handleCustomActionClick($event, actionConfig)"
                                    [disabled]="actionConfig.disabled || false">
                                </p-button>
                                <!-- Text button (e.g., "Fetch GST") -->
                                <p-button 
                                    *ngIf="actionConfig.show && !actionConfig.svgIcon && actionConfig.label"
                                    [label]="actionConfig.label"
                                    [outlined]="true"
                                    size="small"
                                    (onClick)="handleCustomActionClick($event, actionConfig)"
                                    [disabled]="actionConfig.disabled || false">
                                </p-button>
                            </ng-container>
                        </td>

                        <!-- Delete button (only in edit mode) - always at the end -->
                        <td class="text-center remove-button-column" *ngIf="!viewMode && showAddRemoveButtons && !(shouldHideDeleteButton && shouldHideDeleteButton(rowData.control))" style="width: 80px; min-width: 80px; position: sticky; right: 0; background-color: #fafbfc; z-index: 10;">
                            <p-button icon="pi pi-times" [rounded]="true"
                                [outlined]="true" [text]="true" (click)="removeTableRow(rowData.index)">
                            </p-button>
                        </td>
                    </tr>
                </ng-template>

                <!-- Totals Row -->
                <ng-template pTemplate="footer" *ngIf="shouldShowTotalsRow()">
                    <tr class="totals-row">
                        <!-- Selection Checkbox Column - Empty cell -->
                        <td *ngIf="showSelectionCheckbox && !viewMode" class="text-sm text-center" style="width: 60px; min-width: 60px;">
                        </td>
                        <!-- Serial Number Column - Empty cell -->
                        <td *ngIf="showSerialNumber" class="text-sm text-center" style="width: 80px; min-width: 80px;">
                        </td>
                        <!-- Duplicate button column - Empty cell (at front) -->
                        <td *ngIf="!viewMode && showDuplicateRowButton" class="duplicate-button-column" style="width: 50px; min-width: 50px; background-color: #fafbfc;">
                        </td>
                        <ng-container *ngFor="let col of dynamicFormFields; let first = first">
                            <td *ngIf="col.is_visible !== false && (!shouldHideField || !shouldHideField(col, undefined, undefined))"
                                class="text-sm font-semibold"
                                [class.text-right]="shouldShowTotalForField(col)"
                                [class.text-left]="!shouldShowTotalForField(col) && first"
                                [style.width]="getColumnWidth(col)"
                                [style.min-width]="getColumnMinWidth(col)">
                                <!-- Show "Total" label in first column -->
                                <span *ngIf="first && !shouldShowTotalForField(col)" class="text-gray-900">
                                    Total
                                </span>
                                <!-- Show total value for numeric fields -->
                                <span *ngIf="shouldShowTotalForField(col)" class="text-gray-900">
                                    {{ formatCurrencyWithUSD(calculateTotal(col.field_name)) }}
                                </span>
                                <!-- Empty cell for non-total fields (except first) -->
                                <span *ngIf="!first && !shouldShowTotalForField(col)"></span>
                            </td>
                        </ng-container>
                        <!-- Extra columns - Empty cells in totals row -->
                        <ng-container *ngIf="extraColumns">
                            <td *ngFor="let extraCol of extraColumns"></td>
                        </ng-container>
                        <!-- Add button column - Empty cell -->
                        <td *ngIf="!viewMode && showAddRemoveButtons && showAddButton" class="add-button-column" style="width: 80px; min-width: 80px; position: sticky; right: 0; background-color: #fafbfc; z-index: 10;">
                        </td>
                    </tr>
                </ng-template>

            </p-table>
        </div>

        <div *ngIf="showPagination && totalPages > 0" class="flex justify-center items-center gap-3 py-3">
            <p-button icon="pi pi-angle-left" [disabled]="currentPage <= 1 || paginationLoading"
                styleClass="p-button-text p-button-sm" (onClick)="pageChange.emit(currentPage - 1)">
            </p-button>
            <span class="text-sm text-gray-600">Page {{ currentPage }} of {{ totalPages }}</span>
            <p-button icon="pi pi-angle-right" [disabled]="currentPage >= totalPages || paginationLoading"
                styleClass="p-button-text p-button-sm" (onClick)="pageChange.emit(currentPage + 1)">
            </p-button>
        </div>

        <div class="flex justify-start gap-2 mt-4" *ngIf="!viewMode && showBulkActionButtons">
            <p-button label="Clear All Lines" (onClick)="deleteAllRows()" styleClass="bulk-action-btn" />
            <p-button label="Copy Previous" (onClick)="duplicateLastRow()" styleClass="bulk-action-btn" />
        </div>

    </ng-container>
</form>